import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Helmet } from "react-helmet";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { supabase } from "@/integrations/supabase/client";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load components with dynamic imports
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const JudgesDashboard = lazy(() => import("./pages/judges/JudgesDashboard"));
const Teams = lazy(() => import("./pages/judges/Teams"));
const Project = lazy(() => import("./pages/Project"));
const JudgesLayout = lazy(() => import("./components/layouts/JudgesLayout"));
const AdminLayout = lazy(() => import("./components/layouts/AdminLayout"));
const SubmitProject = lazy(() => import("./pages/SubmitProject"));
const SubmissionSuccess = lazy(() => import("./pages/SubmissionSuccess"));
const SubmitRedirect = lazy(() => import("./pages/SubmitRedirect"));
const JudgeManagement = lazy(() => import("./pages/admin/JudgeManagement"));

// Admin hackathon related pages
const HackathonManagement = lazy(() => import("./pages/admin/HackathonManagement"));
const HackathonDetailsPage = lazy(() => import("./pages/admin/HackathonDetailsPage"));
const EditHackathonPage = lazy(() => import("./pages/admin/EditHackathonPage"));
const HackathonSubmissions = lazy(() => import("./pages/admin/HackathonSubmissions"));
const HackathonScoreReport = lazy(() => import("./pages/admin/HackathonScoreReport"));
const HackathonScoreManagement = lazy(() => import("./pages/admin/HackathonScoreManagement"));
const ProjectDetails = lazy(() => import("./pages/admin/ProjectDetails"));
const HackathonCriteria = lazy(() => import("./pages/admin/HackathonCriteria"));

// Authentication wrapper component
function AuthWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();

  useEffect(() => {
    // Set up a single global auth listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' && window.location.pathname !== '/auth') {
        navigate("/auth");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Helmet>
        <title>HackScore - Hackathon Judging Platform</title>
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
            <Suspense
              fallback={
                <div className="flex items-center justify-center min-h-screen">
                  <LoadingSpinner />
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="hackathons" element={<HackathonManagement />} />
                  <Route path="judges" element={<JudgeManagement />} />

                  {/* Hackathon specific routes */}
                  <Route path="hackathon/:hackathonId" element={<HackathonDetailsPage />} />
                  <Route path="hackathons/edit/:hackathonId" element={<EditHackathonPage />} />
                  <Route path="hackathon/:hackathonId/submissions" element={<HackathonSubmissions />} />
                  <Route path="hackathon/:hackathonId/score-report" element={<HackathonScoreReport />} />
                  <Route path="hackathon/:hackathonId/score-management" element={<HackathonScoreManagement />} />
                  <Route path="hackathon/:hackathonId/criteria" element={<HackathonCriteria />} />
                  <Route path="project/:projectId" element={<ProjectDetails />} />
                </Route>
                <Route path="/judges" element={<JudgesLayout />}>
                  <Route index element={<JudgesDashboard />} />
                  <Route path=":hackathonId/teams" element={<Teams />} />
                  <Route path=":hackathonId/:teamId/project/:projectId" element={<Project />} />
                </Route>
                <Route path="/hackathon/:hackathonId/submit" element={<SubmitProject />} />
                <Route path="/submission-success" element={<SubmissionSuccess />} />
              </Routes>
            </Suspense>
            <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
